import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from '../api/stripe';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import api from '../api/axiosInstance';
import { errText, httpStatus } from '../api/errors';
import { formatPeriodEnd } from './subscriptionPeriod';
import { isManageable, isPaidThroughADate as paidThroughADate, needsPayment as chargeFailed, statusLabel } from './subscriptionStatus';
import UpdatePaymentMethod from './UpdatePaymentMethod';

interface PaymentMethodDto {
    id: string;
    cardBrand: string;
    cardLast4: string;
    expMonth: number;
    expYear: number;
}

interface SubscriptionDetails {
    id: string | null;
    status: string;
    currentPeriodEnd: number; // Unix timestamp in seconds
    paymentMethod?: PaymentMethodDto;
    premiumEndDate?: number; // Unix timestamp in seconds - for one-time payments
    isRecurring?: boolean; // Whether this is a recurring subscription or one-time payment
    daysRemaining?: number; // Calculated days remaining
    /**
     * The member has cancelled and the subscription is running out the period they already paid
     * for: Stripe still reports `status: "active"` until `currentPeriodEnd`, and no further charge
     * is made. Optional because a jar older than the monthly-only release does not send it, and an
     * absent field must read as "not cancelled" rather than as `undefined`.
     */
    cancelAtPeriodEnd?: boolean;
}


const Container = styled.div`
    padding: 20px;
    background: #f8f9fa;
    border-radius: 8px;
    max-width: 600px;
    margin: 20px auto;
`;

const Title = styled.h2`
    margin-bottom: 20px;
    text-align: center;
`;

const InfoRow = styled.div`
    margin-bottom: 16px;
    font-size: 1rem;
    color: #333;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Label = styled.span`
    font-weight: bold;
`;

/** Green while the membership is running, amber once it has been cancelled and is counting down to
 *  the paid date, red otherwise. */
const StatusBadge = styled.div<{ status: string; $endingSoon?: boolean }>`
    background: ${({ status, $endingSoon }) =>
            $endingSoon ? '#fd7e14' : status.toLowerCase() === 'active' ? '#28a745' : '#dc3545'};
    /* past_due and friends land on red deliberately: the badge is not the explanation, the warning
       box under it is, and amber here would read as "cancelled" next to a member who is not. */
    color: #fff;
    font-weight: bold;
    padding: 6px 12px;
    border-radius: 12px;
    font-size: 1rem;
`;

/** The one state that needs an explanation rather than a status word: the card was declined. */
const PaymentWarning = styled.div`
    margin-top: 20px;
    padding: 12px 14px;
    border-radius: 8px;
    background: #fff4e5;
    border: 1px solid #f0b27a;
    color: #7a4b12;
    font-size: 0.95rem;
    line-height: 1.45;
`;

const Button = styled.button`
    background-color: #f56607;
    color: white;
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 1rem;
    width: 100%;
    margin-top: 24px;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: #d75405;
    }

    &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
    }
`;

/** The two buttons inside the cancel confirmation. They sit closer together than a standalone
 *  action, so they read as one question with two answers. */
const DangerButton = styled(Button)`
    background-color: #dc3545;
    margin-top: 12px;

    &:hover {
        background-color: #c82333;
    }
`;

const SecondaryButton = styled(Button)`
    background-color: #fff;
    color: #333;
    border: 1px solid #ccc;
    margin-top: 12px;

    &:hover {
        background-color: #f0f0f0;
    }
`;

/**
 * Inline feedback, red by default and green with `$ok`.
 *
 * This screen used to `setError(...)` on a failed cancellation and then render the message nowhere:
 * the only place `error` was read returned early, and only when there was no subscription to show.
 * A member whose cancellation had just failed saw the unchanged screen and no explanation.
 */
const Banner = styled.p<{ $ok?: boolean }>`
    background: ${({ $ok }) => ($ok ? '#eef7ee' : '#fdecec')};
    color: ${({ $ok }) => ($ok ? '#256029' : '#a11')};
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 0.95rem;
    line-height: 1.6;
    margin: 0 0 16px;
`;

/** The "you have cancelled, here is until when" notice. Amber, not red: nothing is wrong. */
const CancelNotice = styled.div`
    background: #fff4e5;
    color: #8a5300;
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 0.95rem;
    line-height: 1.6;
    margin-top: 24px;
`;

const ConfirmBox = styled.div`
    border-top: 1px solid #e2e4e6;
    margin-top: 24px;
    padding-top: 16px;
`;

const ConfirmText = styled.p`
    font-size: 0.95rem;
    line-height: 1.6;
    color: #333;
    margin: 0;
`;

// Virtual card styling to mimic a physical credit card appearance.
const VirtualCardWrapper = styled.div`
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 16px;
    width: 320px;
    height: 200px;
    color: #fff;
    padding: 20px;
    position: relative;
    font-family: 'Courier New', Courier, monospace;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    margin: 20px auto;
`;

const CardBrand = styled.div`
    font-size: 1rem;
    font-weight: bold;
`;

const CardNumber = styled.div`
    font-size: 1rem;
    letter-spacing: 4px;
    margin-top: 20px;
    text-align: center;
    white-space: nowrap;
`;

const Expiration = styled.div`
    font-size: 1rem;
    margin-top: 20px;
    text-align: left;
`;

const CardHolder = styled.div`
    font-size: 1rem;
    position: absolute;
    bottom: 20px;
    left: 20px;
    text-align: left;
`;

const VirtualCard: React.FC<{ paymentMethod: PaymentMethodDto; cardHolderName?: string }> = ({
                                                                                                 paymentMethod,
                                                                                                 cardHolderName,
                                                                                             }) => {
    return (
        <VirtualCardWrapper>
            <CardBrand>{paymentMethod.cardBrand.toUpperCase()}</CardBrand>
            <CardNumber>**** **** **** {paymentMethod.cardLast4}</CardNumber>
            <Expiration>
                Exp: {paymentMethod.expMonth.toString().padStart(2, '0')}/{paymentMethod.expYear}
            </Expiration>
            {cardHolderName && <CardHolder>{cardHolderName}</CardHolder>}
        </VirtualCardWrapper>
    );
};

const SubscriptionManager: React.FC = () => {
    const user = useSelector((state: RootState) => state.auth.user);
    const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [canceling, setCanceling] = useState<boolean>(false);
    const [resuming, setResuming] = useState<boolean>(false);
    const [confirmingCancel, setConfirmingCancel] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);

    const fetchSubscription = async () => {
        if (!user?.id) {
            setError('Používateľ sa nenašiel');
            setLoading(false);
            return;
        }
        try {
            const response = await api.get(`/subscription/${user.id}`);
            setSubscription(response.data);
            setError(null); // Clear any previous errors
        } catch (err: any) {
            setSubscription(null);
            // Every 400 from this endpoint means "there is nothing to show for this member" — no
            // Stripe subscription, no unexpired one-off pass. That is a state, not a failure, and
            // it is recognised by the status rather than by the Slovak sentence in the body: the
            // sentence is plain text (not the JSON envelope the rest of the API sends), so an
            // exact-string match breaks the moment somebody rewords it server-side.
            //
            // Everything else IS a failure and now says so. Reading a 500 or a dropped connection
            // as "Momentálne nemáte aktivované členstvo." told paying members their membership was
            // gone whenever the API hiccuped.
            setError(httpStatus(err) === 400
                ? null
                : errText(err, 'Údaje o predplatnom sa nepodarilo načítať. Skúste to znova.'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubscription();
    }, [user]);

    /**
     * Cancel at period end: Stripe stops renewing, the member keeps the access they paid for, and
     * `customer.subscription.deleted` takes the entitlement away when the period actually ends.
     * Nothing here revokes anything.
     */
    const handleCancelSubscription = async () => {
        setCanceling(true);
        setError(null);
        setNotice(null);
        try {
            await api.post(`/subscription/cancel/${user?.id}`);
            setConfirmingCancel(false);
            setNotice('Predplatné bolo zrušené. Ďalšia platba sa už nestrhne.');
            // The refetch, not the response body, is what the screen renders: it is the same
            // reading of Stripe that a reload would give, and it also refreshes the card on file.
            await fetchSubscription();
        } catch (err) {
            setError(errText(err, 'Predplatné sa nepodarilo zrušiť. Skúste to znova.'));
        } finally {
            setCanceling(false);
        }
    };

    /** Undo a cancellation before the paid period runs out: renewals start again, same card. */
    const handleResumeSubscription = async () => {
        setResuming(true);
        setError(null);
        setNotice(null);
        try {
            await api.post(`/subscription/resume/${user?.id}`);
            setNotice('Predplatné bolo obnovené. Bude sa ďalej automaticky obnovovať.');
            await fetchSubscription();
        } catch (err) {
            setError(errText(err, 'Predplatné sa nepodarilo obnoviť. Skúste to znova.'));
        } finally {
            setResuming(false);
        }
    };

    if (loading) {
        return <Container>Načítavajú sa údaje o predplatnom...</Container>;
    }

    if (!subscription) {
        // The notice is rendered here too: a cancellation that went through and was then followed
        // by a failed refetch must still say that it went through.
        return (
            <Container>
                <Title>Správa predplatného</Title>
                {error && <Banner>{error}</Banner>}
                {notice && <Banner $ok>{notice}</Banner>}
                {!error && !notice && <InfoRow>Momentálne nemáte aktivované členstvo.</InfoRow>}
            </Container>
        );
    }

    // Calculate days remaining for one-time payments
    const getDaysRemaining = (): number | null => {
        if (subscription.daysRemaining !== undefined) {
            return subscription.daysRemaining;
        }
        if (subscription.premiumEndDate) {
            const now = Math.floor(Date.now() / 1000);
            const endDate = subscription.premiumEndDate;
            const diffInSeconds = endDate - now;
            const diffInDays = Math.ceil(diffInSeconds / (24 * 60 * 60));
            return diffInDays > 0 ? diffInDays : 0;
        }
        return null;
    };

    const daysRemaining = getDaysRemaining();
    const status = (subscription.status || '').toLowerCase();

    /**
     * A legacy one-off pass: the 1-day, 3-month and yearly passes were retired from sale on the
     * monthly-only cutover, but their holders run to term — the last yearly one expires around
     * September 2027, and until then this display is the only thing that describes their
     * membership correctly.
     */
    const isOneTimePayment = subscription.isRecurring === false || status === 'one_time';

    // For one-time payments, check if premium is still valid (daysRemaining >= 0 means at least today)
    const isOneTimeActive = isOneTimePayment && daysRemaining !== null && daysRemaining >= 0;

    /**
     * The renewal charge failed and Stripe is retrying the card. The predicates themselves live in
     * `subscriptionStatus.ts`, which imports nothing and is therefore the only place in this file's
     * logic that Jest can reach (CRA 5's jest cannot import axios, and this module can).
     */
    const needsPayment = chargeFailed(status);

    /** A Stripe subscription the member can still act on — cancel, or replace the card. */
    const isRecurringActive = !isOneTimePayment && isManageable(status);

    /** Only some of those have a paid period running; the rest have a charge that did not go through. */
    const isPaidThroughADate = isRecurringActive && paidThroughADate(status);

    /** Cancelled and running out the paid period. Absent on an older jar, and absent is not
     *  cancelled. Meaningless while a charge is outstanding: there is no paid period to run out. */
    const cancelAtPeriodEnd = isPaidThroughADate && subscription.cancelAtPeriodEnd === true;

    /** The day the paid period runs to, or null when Stripe sent none. */
    const periodEnd = formatPeriodEnd(subscription.currentPeriodEnd);

    /** The same thing inside a sentence, which still has to read as one when the date is missing. */
    const periodEndText = periodEnd || 'konca zaplateného obdobia';

    // Determine the effective status for badge color
    const effectiveStatus = isOneTimeActive ? 'active' : subscription.status || '';

    const changePaymentMethodButton = (
        <Button
            onClick={() => setShowUpdateModal(true)}
            style={{ marginTop: '12px', backgroundColor: '#007bff' }}
        >
            Zmeniť platobnú metódu
        </Button>
    );

    return (
        <Container>
            <Title>Správa predplatného</Title>
            {error && <Banner>{error}</Banner>}
            {notice && <Banner $ok>{notice}</Banner>}
            <InfoRow>
                <Label>Stav:</Label>
                <StatusBadge status={effectiveStatus} $endingSoon={cancelAtPeriodEnd}>
                    {isOneTimeActive
                        ? 'AKTÍVNE'
                        : cancelAtPeriodEnd
                            ? 'ZRUŠENÉ'
                            : statusLabel(status)}
                </StatusBadge>
            </InfoRow>
            {isOneTimeActive ? (
                <InfoRow>
                    <Label>Prístup do:</Label>
                    <span>{formatPeriodEnd(subscription.premiumEndDate) || 'Neznámy dátum'}</span>
                </InfoRow>
            ) : isPaidThroughADate && periodEnd ? (
                <InfoRow>
                    {/* Once it is cancelled there is no next billing date: the same timestamp is
                        the day the access stops. */}
                    <Label>{cancelAtPeriodEnd ? 'Prístup do:' : 'Ďalší dátum fakturácie:'}</Label>
                    <span>{periodEnd}</span>
                </InfoRow>
            ) : null}
            {isOneTimeActive && daysRemaining !== null && (
                <InfoRow>
                    <Label>Zostáva dní:</Label>
                    <span style={{ fontWeight: 'bold', color: '#28a745' }}>
                        {daysRemaining === 0 ? 'menej ako 1 deň' : `${daysRemaining} ${daysRemaining === 1 ? 'deň' : daysRemaining < 5 ? 'dni' : 'dní'}`}
                    </span>
                </InfoRow>
            )}
            {subscription.paymentMethod && (
                <VirtualCard
                    paymentMethod={subscription.paymentMethod}
                    cardHolderName={user?.fullName}
                />
            )}
            {needsPayment && (
                <PaymentWarning>
                    Poslednú platbu sa nepodarilo strhnúť, preto je členstvo pozastavené. Aktualizujte
                    platobnú kartu — po úspešnej platbe sa prístup automaticky obnoví. Ak už predplatné
                    nechcete, môžete ho zrušiť nižšie.
                </PaymentWarning>
            )}
            {isRecurringActive ? (
                needsPayment ? (
                    <>
                        {changePaymentMethodButton}
                        {confirmingCancel ? (
                            <ConfirmBox>
                                <ConfirmText>
                                    Naozaj chcete zrušiť predplatné? Zrušením sa zastavia ďalšie pokusy
                                    o strhnutie platby.
                                </ConfirmText>
                                <DangerButton onClick={handleCancelSubscription} disabled={canceling}>
                                    {canceling ? 'Ruší sa...' : 'Áno, zrušiť predplatné'}
                                </DangerButton>
                                <SecondaryButton onClick={() => setConfirmingCancel(false)} disabled={canceling}>
                                    Ponechať predplatné
                                </SecondaryButton>
                            </ConfirmBox>
                        ) : (
                            <SecondaryButton onClick={() => setConfirmingCancel(true)}>
                                Zrušiť predplatné
                            </SecondaryButton>
                        )}
                    </>
                ) : cancelAtPeriodEnd ? (
                    <>
                        <CancelNotice>
                            Predplatné je zrušené, prístup máte do {periodEndText}.
                        </CancelNotice>
                        <Button onClick={handleResumeSubscription} disabled={resuming}>
                            {resuming ? 'Obnovuje sa...' : 'Obnoviť predplatné'}
                        </Button>
                        {changePaymentMethodButton}
                    </>
                ) : confirmingCancel ? (
                    // Asked here rather than through `window.confirm`: the sentence carries the
                    // date the access runs to, and a browser dialog is the one place a member is
                    // most likely to click through without reading it.
                    <ConfirmBox>
                        <ConfirmText>
                            Naozaj chcete zrušiť predplatné? Ďalšia platba sa už nestrhne a prístup vám
                            zostane do {periodEndText}.
                        </ConfirmText>
                        <DangerButton onClick={handleCancelSubscription} disabled={canceling}>
                            {canceling ? 'Ruší sa...' : 'Áno, zrušiť predplatné'}
                        </DangerButton>
                        <SecondaryButton onClick={() => setConfirmingCancel(false)} disabled={canceling}>
                            Ponechať predplatné
                        </SecondaryButton>
                    </ConfirmBox>
                ) : (
                    <>
                        <Button onClick={() => setConfirmingCancel(true)}>Zrušiť predplatné</Button>
                        {changePaymentMethodButton}
                    </>
                )
            ) : isOneTimeActive ? (
                <InfoRow style={{ marginTop: '20px', textAlign: 'center', color: '#28a745', fontWeight: 'bold' }}>
                    {daysRemaining === 0
                        ? 'Máte prístup do konca dnešného dňa'
                        : `Máte prístup na ${daysRemaining} ${daysRemaining === 1 ? 'deň' : daysRemaining < 5 ? 'dni' : 'dní'}`}
                </InfoRow>
            ) : (
                <InfoRow>Vaše predplatné nie je aktívne.</InfoRow>
            )}
            {showUpdateModal && (
                <Elements stripe={stripePromise}>
                    <UpdatePaymentMethod onClose={() => setShowUpdateModal(false)} />
                </Elements>
            )}
        </Container>
    );
};

export default SubscriptionManager;